import { HomeMarqueeSectionPropType } from "@/components/organisms/Carousels/HomeMarqueeSection";
import { FooterDataType } from "@/components/organisms/Footer";
import GraphyHead from "@/components/organisms/GraphyHead";
import { HomeV2DataType } from "@/shared/data/home.v2.data";
import { NavbarDataType } from "@/shared/data/navbar.data";
import Head from "next/head";

import AiTeamSection from "./AiTeamSection";
import AutomationsSection from "./AutomationsSection";
import CreatorsMarqueeV2 from "./CreatorsMarqueeV2";
import CtaBannerV2 from "./CtaBannerV2";
import FeaturesGridV2 from "./FeaturesGridV2";
import FooterV2 from "./FooterV2";
import HeroV2 from "./HeroV2";
import IdeaToIncomeSection from "./IdeaToIncomeSection";
import NavbarV2 from "./NavbarV2";
import PlatformTabsSection from "./PlatformTabsSection";

export type HomeV2Props = {
  data: HomeV2DataType;
  /**
   * Navbar data for the legacy Navbar — accepted but unused on the new
   * homepage (NavbarV2 has its own hardcoded menu). Keeps the call-site in
   * pages/index.tsx identical for both code paths.
   */
  navbarData: NavbarDataType;
  /**
   * Footer data for the legacy Footer — accepted but unused on the new
   * homepage (FooterV2 is driven by data.footer). Keeps the call-site in
   * pages/index.tsx identical for both code paths.
   */
  footerData: FooterDataType;
  marqueeProps: HomeMarqueeSectionPropType;
  countryCode: string;
};

const HomeV2 = ({ data, marqueeProps, countryCode }: HomeV2Props) => {
  return (
    <>
      <GraphyHead {...data.seoData} countryCode={countryCode} />
      <Head>
        <meta
          name="naver-site-verification"
          content="6501b1d7fd9fb966bf1fb46e57f090f383564a6a"
        />
      </Head>
      {/* Top-of-page gradient wash — lives on a wrapper that covers both
          the sticky navbar AND the top of the hero so the pink/peach wash
          reads as one continuous background, not as a gradient that starts
          under the navbar. When the user scrolls, this gradient scrolls
          away and the navbar's own glass-effect background takes over. */}
      <div className="home-v2-scope relative">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[600px]"
          style={{
            // Dual-tone brand wash. The purple (brand primary, #5766F2)
            // dominates the hero and the warm yellow (yellow.1, #FFDF70)
            // peaks in the top-right ~20% — but both ellipses have long,
            // low-opacity tails that cross into each other's territory so
            // the mid-area reads as a smooth purple→yellow blend instead
            // of two isolated hotspots. A subtle linear gradient sits on
            // top to ease the horizontal transition further.
            background:
              "linear-gradient(90deg, rgba(87, 102, 242, 0.08) 0%, rgba(87, 102, 242, 0.02) 45%, rgba(255, 223, 112, 0.04) 70%, rgba(255, 223, 112, 0.14) 100%)," +
              "radial-gradient(ellipse 85% 65% at 25% 0%, rgba(87, 102, 242, 0.34) 0%, rgba(87, 102, 242, 0.16) 30%, rgba(87, 102, 242, 0.05) 55%, rgba(255, 255, 255, 0) 80%)," +
              "radial-gradient(ellipse 55% 70% at 95% 0%, rgba(255, 223, 112, 0.55) 0%, rgba(255, 223, 112, 0.28) 18%, rgba(255, 223, 112, 0.1) 38%, rgba(255, 223, 112, 0.03) 60%, rgba(255, 255, 255, 0) 82%)",
            maskImage:
              "linear-gradient(to bottom, black 0%, black 70%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, black 0%, black 70%, transparent 100%)",
          }}
        />
        <NavbarV2 />

        <main className="relative z-10 flex flex-col">
          <HeroV2 {...data.hero} />
          <PlatformTabsSection {...data.platformTabs} />
          <IdeaToIncomeSection {...data.ideaToIncome} />
          <AiTeamSection {...data.aiTeam} />
          <AutomationsSection {...data.automations} />
          <CreatorsMarqueeV2
            {...data.creatorsGrid}
            marqueeProps={marqueeProps}
          />
          <FeaturesGridV2 {...data.featuresGrid} />
          <CtaBannerV2 {...data.ctaBanner} />
        </main>
      </div>

      <FooterV2 {...data.footer} />
    </>
  );
};

export default HomeV2;
